import React from 'react';

/** Militärgrön sidbakgrund i mörkt läge (redesign okt 2026). */
export const MOSS_PAGE_BG = '#2f3827';

/** Typsnitt: Hanken Grotesk för UI, Instrument Serif för logotyp och display. */
export const FONT_UI = "'Hanken Grotesk', system-ui, -apple-system, sans-serif";
export const FONT_SERIF = "'Instrument Serif', Georgia, serif";

// Färgroller (DR1 §2.3). Gamla namn (amber, success, danger …) finns kvar så att befintliga vyer
// fungerar. Ny kod använder rollerna:
//   accentFill + onAccent  – fylld primärknapp, aktiv pill, på-reglage
//   accentText             – orange text (länkar, aktiv menypunkt, siffror)
//   amber                  – orange som dekor (gräns, kartnålar, understrykning)
//   ok/danger/info (+Soft) – statusfärger som text, *Soft som bakgrund bakom dem
// Alla textroller klarar WCAG AA (4,5:1) mot bg, surface och surface2 – se theme.test.ts.

export const DARK_THEME = {
  bg: MOSS_PAGE_BG, surface: '#3a4531', surface2: '#455139', surfaceHover: '#455139',
  border: 'rgba(241,242,233,0.14)', borderHover: 'rgba(241,242,233,0.28)',
  text: '#f1f2e9', textSub: '#c3c9b3', textMuted: '#bac1a8',
  amber: '#e5882a', amberLight: '#f0a050', amberDim: 'rgba(229,136,42,0.18)',
  accentFill: '#e5882a', onAccent: '#1c2117', accentText: '#f5b06c', accentSoft: 'rgba(229,136,42,0.18)',
  ok: '#98d886', okSoft: 'rgba(152,216,134,0.16)', onOk: '#1c2117',
  danger: '#f5aea6', dangerSoft: 'rgba(245,174,166,0.16)', onDanger: '#1c2117',
  info: '#95c2ee', infoSoft: 'rgba(149,194,238,0.16)',
  success: '#61D836', forest: '#4E594C',
  accent1: '#00A2FF', accent2: '#16E7CF', accent3: '#61D836',
  focus: '#f5b06c',
  shadow: {
    card: '0 1px 3px rgba(0,0,0,0.3)', float: '0 2px 8px rgba(0,0,0,0.32)',
    raised: '0 10px 30px rgba(0,0,0,0.3)', sheet: '0 -6px 24px rgba(0,0,0,0.35)',
  },
  overlay: 'rgba(47,56,39,0.9)', scrim: 'rgba(0,0,0,0.55)', navH: 56,
  map: '#3d4833', water: '#344a44', switchBg: '#e5882a', segEmpty: 'rgba(241,242,233,0.14)',
};

export const LIGHT_THEME: typeof DARK_THEME = {
  bg: '#f5f4ef', surface: '#ffffff', surface2: '#efeee7', surfaceHover: '#efeee7',
  border: 'rgba(28,33,23,0.12)', borderHover: 'rgba(28,33,23,0.26)',
  text: '#1c2117', textSub: '#5c6253', textMuted: '#63685a',
  amber: '#c96a0c', amberLight: '#e5882a', amberDim: '#fbeedd',
  accentFill: '#b05d0a', onAccent: '#ffffff', accentText: '#a04f06', accentSoft: '#fbeedd',
  ok: '#2a6a2e', okSoft: '#e6f2e6', onOk: '#ffffff',
  danger: '#b3261e', dangerSoft: '#fbe9e7', onDanger: '#ffffff',
  info: '#2b6cb0', infoSoft: '#e5eef8',
  success: '#2a6a2e', forest: '#4E594C',
  accent1: '#2b6cb0', accent2: '#10b8a0', accent3: '#2a6a2e',
  focus: '#a04f06',
  shadow: {
    card: '0 1px 3px rgba(0,0,0,0.12)', float: '0 2px 8px rgba(0,0,0,0.16)',
    raised: '0 10px 30px rgba(0,0,0,0.08)', sheet: '0 -6px 24px rgba(0,0,0,0.18)',
  },
  overlay: 'rgba(245,244,239,0.92)', scrim: 'rgba(28,33,23,0.45)', navH: 56,
  map: '#e6e9de', water: '#cadbdf', switchBg: '#c9ccbf', segEmpty: '#e2e2da',
};

export type Theme = typeof DARK_THEME;

/** Radier: 12 knappar/fält, 16 kort/kartor, 22 ark. */
export const RADIUS = { xs: 4, sm: 8, md: 12, lg: 16, xl: 22, pill: 999 } as const;
/** Avståndsskala i px. */
export const SPACE = [4, 8, 12, 16, 20, 24, 32, 40, 56] as const;
/** Minsta tryckyta (px) för allt interaktivt. */
export const TAP = 44;

/** Typskala (DR1 §2.3). Ersätter 9, 10, 11,5, 12,5, 13,5, 17 och vikt 300. */
export const TYPE = {
  label: { fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' },
  small: { fontSize: 13, fontWeight: 500 },
  body: { fontSize: 15, fontWeight: 500 },
  bodyL: { fontSize: 16, fontWeight: 400, lineHeight: 1.6 },
  title: { fontSize: 18, fontWeight: 700 },
  section: { fontSize: 22, fontWeight: 800 },
  page: { fontSize: 32, fontWeight: 800, letterSpacing: '-0.02em' },
  h1: { fontSize: 40, fontWeight: 800, letterSpacing: '-0.02em' },
} as const satisfies Record<string, React.CSSProperties>;

/** Tokens som CSS-variabler (`--q-bg`, `--q-accent-fill` …) för Leaflet-popups och global CSS. */
export function themeCssVars(T: Theme): string {
  const kebab = (k: string) => k.replace(/[A-Z0-9]/g, (c) => '-' + c.toLowerCase());
  const lines: string[] = [];
  for (const [k, v] of Object.entries(T)) {
    if (typeof v === 'string') lines.push(`--q-${kebab(k)}: ${v};`);
  }
  for (const [k, v] of Object.entries(T.shadow)) lines.push(`--q-shadow-${k}: ${v};`);
  for (const [k, v] of Object.entries(RADIUS)) lines.push(`--q-radius-${k}: ${v}px;`);
  lines.push(`--q-font-ui: ${FONT_UI};`, `--q-font-serif: ${FONT_SERIF};`);
  return `:root{${lines.join('')}}`;
}

export type ThemeMode = 'dark' | 'light';
export const ThemeCtx = React.createContext<{ mode: ThemeMode; toggle: () => void }>({ mode: 'dark', toggle: () => {} });
export function getTheme(m: ThemeMode): Theme { return m === 'dark' ? DARK_THEME : LIGHT_THEME; }
export function useThemeMode(): { mode: ThemeMode; toggle: () => void } { return React.useContext(ThemeCtx); }
/** Aktuellt tema som objekt. */
export function useTheme(): Theme { return getTheme(useThemeMode().mode); }
